import { hasAnalyticsConsent } from "./consent"
import type { ConsentDecision } from "./consent"
import { hasResolvableTag } from "./tags"

/**
 * Configuração agnóstica de provider. Ambos os ids são públicos (NEXT_PUBLIC_) e
 * opcionais: vazios ⇒ nenhum provider ligado (scaffold dormant até o cutover, Épico 9).
 */
export type AnalyticsConfig = {
  measurementId?: string
  pixelId?: string
}

/**
 * Normaliza os valores crus de env: trim e vazio→undefined. Puro e testável — recebe os
 * valores como ARGUMENTOS (o teste exercita isto). Ver GOTCHA em readEnvConfig sobre por
 * que a leitura da env é separada da normalização.
 */
export function normalizeConfig(
  rawMeasurement?: string,
  rawPixel?: string
): AnalyticsConfig {
  return {
    measurementId: normalizeId(rawMeasurement),
    pixelId: normalizeId(rawPixel),
  }
}

function normalizeId(raw?: string): string | undefined {
  const trimmed = raw?.trim()
  return trimmed ? trimmed : undefined
}

/**
 * GOTCHA (Next NEXT_PUBLIC_ inlining): o Next só inlina no bundle do browser referências
 * ESTÁTICAS literais `process.env.NEXT_PUBLIC_FOO`. Um acesso por chave computada
 * (`process.env[key]`) NÃO é inlinado e vem `undefined` no cliente — deixaria a config
 * sempre vazia e o gate sempre falso mesmo com env preenchida. Por isso lemos aqui com
 * refs literais; a lógica pura (normalizeConfig) recebe os valores como argumentos e é o
 * que os testes cobrem. readEnvConfig é acesso estático fino — não testado (como os
 * outros reads de env do projeto).
 */
export function readEnvConfig(): AnalyticsConfig {
  return normalizeConfig(
    process.env.NEXT_PUBLIC_ANALYTICS_MEASUREMENT_ID,
    process.env.NEXT_PUBLIC_ANALYTICS_PIXEL_ID
  )
}

export function getAnalyticsConfig(): AnalyticsConfig {
  return readEnvConfig()
}

/**
 * Um provider está configurado quando a config resolve pelo menos um TAG CARREGÁVEL.
 *
 * MUDANÇA DE CONTRATO (Story 10.10, deliberada): a 2.6 respondia `true` para qualquer id
 * não-vazio, incluindo um `pixelId` sozinho — apareceria banner, o cliente consentiria e
 * nada carregaria. Pedir consentimento para um tag que não existe é pior do que não pedir,
 * por isso a pergunta passou a ser respondida pelo registo de tags. Com env vazia (hoje)
 * continua sempre falso ⇒ scaffold inerte (sem banner, sem disparo).
 *
 * Delega no predicado BARATO do registo: este gate corre a cada render do banner e do rodapé
 * e a cada evento — construir objetos de tag só para os contar era custo puro.
 */
export function hasConfiguredProvider(config: AnalyticsConfig): boolean {
  return hasResolvableTag(config)
}

/**
 * Duplo gate (loader-gating): só carrega/dispara com consentimento afirmativo E provider
 * configurado. Fail-closed por construção.
 */
export function shouldLoadAnalytics(
  consent: ConsentDecision,
  config: AnalyticsConfig
): boolean {
  return hasAnalyticsConsent(consent) && hasConfiguredProvider(config)
}
